import dayjs from 'dayjs';

import type { LeadsPeriodPresetId, LeadsRegistryDateRange } from './types';

export interface LeadsPeriodPreset {
  readonly id: LeadsPeriodPresetId;
  readonly label: string;
  getRange(): LeadsRegistryDateRange;
}

// Leads-specific date presets. The search filter serializes dates as
// `YYYY-MM-DD` (see formatDayjsToString), so the time part is irrelevant and
// only the calendar day of each bound matters. No task-specific presets here.
export const LEADS_PERIOD_PRESETS: readonly LeadsPeriodPreset[] = [
  {
    id: 'today',
    label: 'Сегодня',
    getRange: () => [dayjs().startOf('day'), dayjs().endOf('day')],
  },
  {
    id: 'yesterday',
    label: 'Вчера',
    getRange: () => [
      dayjs().subtract(1, 'day').startOf('day'),
      dayjs().subtract(1, 'day').endOf('day'),
    ],
  },
  {
    id: 'last3',
    label: 'Последние 3 дня',
    getRange: () => [
      dayjs().subtract(2, 'day').startOf('day'),
      dayjs().endOf('day'),
    ],
  },
  {
    id: 'last7',
    label: 'Последние 7 дней',
    getRange: () => [
      dayjs().subtract(6, 'day').startOf('day'),
      dayjs().endOf('day'),
    ],
  },
  {
    id: 'last10',
    label: 'Последние 10 дней',
    getRange: () => [
      dayjs().subtract(9, 'day').startOf('day'),
      dayjs().endOf('day'),
    ],
  },
  {
    id: 'last30',
    label: 'Последние 30 дней',
    getRange: () => [
      dayjs().subtract(29, 'day').startOf('day'),
      dayjs().endOf('day'),
    ],
  },
  {
    id: 'thisMonth',
    label: 'Этот месяц',
    getRange: () => [dayjs().startOf('month'), dayjs().endOf('month')],
  },
  {
    id: 'prevMonth',
    label: 'Прошлый месяц',
    getRange: () => [
      dayjs().subtract(1, 'month').startOf('month'),
      dayjs().subtract(1, 'month').endOf('month'),
    ],
  },
];

export const LEADS_REGISTRY_DEFAULT_PRESET_ID: LeadsPeriodPresetId = 'last10';

export const getLeadsPeriodPresetById = (
  id: string
): LeadsPeriodPreset | null =>
  LEADS_PERIOD_PRESETS.find((preset) => preset.id === id) ?? null;

// Resolves a preset to its range for the current day; unknown id falls back to
// the default preset so restored state never ends up with an empty period.
export const resolveLeadsPeriodPresetRange = (
  id: LeadsPeriodPresetId
): LeadsRegistryDateRange =>
  (
    getLeadsPeriodPresetById(id) ??
    getLeadsPeriodPresetById(LEADS_REGISTRY_DEFAULT_PRESET_ID)!
  ).getRange();
